import React, { FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import withStyles from 'react-jss';
import { map } from 'ramda';

import { ClassProp } from 'helpers/types';
import { ISO_Format_Date2, parseDate } from 'helpers/date';

import { Timeslice } from 'scenes/Report/Report.helper';
import Query from 'scenes/Report/components/Query';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';

import GET_MERCHANT_PERFORMANCE_YEAR_OVER_YEAR from './YearOverYear.queries';
import {
  MerchantTransaction,
  saleTransformer,
  toStr,
  transactionTransformers,
} from './YearOverYear.helper';
import PlotYearOverYear from './PlotYearOverYear';

import styles from './YearOverYear.styles';

type Props = {
  classes: ClassProp<typeof styles>;
  location: Location;
  height: number;
};

type QueryResponse = {
  merchantPerformanceYearOverYear: MerchantTransaction[];
};

const titleForTransactions = 'Merchant Transactions Year Over Year';
const titleForSales = 'Merchant Sales Year Over Year';

const selectReport = (
  reportList: string,
  props: Props,
  transactions: MerchantTransaction[],
  timeslice: Timeslice,
  years: string[],
  startDate: Date,
  endDate: Date,
  height: number,
) => {
  switch (reportList) {
    case 'salesYearOverYear':
      return (
        <PlotYearOverYear
          {...props}
          transactions={transactions}
          filterByVenueType={false}
          timeslice={timeslice}
          dateRange={{ from: startDate, to: endDate }}
          years={years}
          labelFormat=",d"
          transformer={saleTransformer}
          title={titleForSales}
          height={height}
        />
      );
    case 'salesYearOverYearByVenueType':
      return (
        <PlotYearOverYear
          {...props}
          transactions={transactions}
          filterByVenueType={true}
          timeslice={timeslice}
          dateRange={{ from: startDate, to: endDate }}
          years={years}
          labelFormat=",d"
          transformer={saleTransformer}
          title={titleForSales}
          height={height}
        />
      );
    case 'transactionsYearOverYear':
      return (
        <PlotYearOverYear
          {...props}
          transactions={transactions}
          filterByVenueType={false}
          timeslice={timeslice}
          years={years}
          dateRange={{ from: startDate, to: endDate }}
          labelFormat=".0f"
          transformer={transactionTransformers}
          title={titleForTransactions}
          height={height}
        />
      );
    case 'transactionsYearOverYearByVenueType':
      return (
        <PlotYearOverYear
          {...props}
          transactions={transactions}
          filterByVenueType={true}
          timeslice={timeslice}
          years={years}
          dateRange={{ from: startDate, to: endDate }}
          labelFormat=".0f"
          transformer={transactionTransformers}
          title={titleForTransactions}
          height={height}
        />
      );
    default:
      return null;
  }
};

const defaultHeight = 550;

const renderReport = (props: Props) => (
  { merchantPerformanceYearOverYear: result }: QueryResponse,
  params: ReportParams,
) => {
  const { reportList, endDate, startDate, timeslice } = params;

  const reports = Array.isArray(reportList) ? reportList : [reportList];

  const years = new Set<number>();

  if (endDate && startDate) {
    const parsedEndDate = parseDate(endDate, ISO_Format_Date2);
    const parsedStartDate = parseDate(startDate, ISO_Format_Date2);
    if (parsedEndDate && parsedStartDate) {
      years.add(parsedEndDate.getFullYear());
      years.add(parsedEndDate.getFullYear() - 1);
      years.add(parsedStartDate.getFullYear());
      years.add(parsedStartDate.getFullYear() - 1);
      return reports.map((report, index) => (
        <ReportBase
          batchKey={report}
          header="Transaction Volume"
          params={params}
          key={`${report}_${index}`}
        >
          <>
            {selectReport(
              report,
              props,
              result,
              timeslice as Timeslice,
              map(toStr)(Array.from(years).sort()),
              parsedStartDate,
              parsedEndDate,
              props.height ?? defaultHeight,
            )}
          </>
        </ReportBase>
      ));
    }
  }

  return null;
};

const YearOverYear: FunctionComponent<Props> = (props) => {
  const location = useLocation();
  const params = queryString.parse(location.search) as ReportParams;
  return (
    <Query
      query={GET_MERCHANT_PERFORMANCE_YEAR_OVER_YEAR}
      reportName="Merchant Performance Year Over Year"
      batchKey="performanceYearOverYear"
      queryKey="performanceYearOverYear"
      render={renderReport(props)}
      variables={{
        params,
      }}
    />
  );
};

export default withStyles(styles)(YearOverYear);
